import { useEffect, useMemo, useRef } from "react";
import maplibregl, { type LngLat, type Map as MlMap, type Marker as MlMarker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useUnistyles } from "react-native-unistyles";
import { View } from "@src/components";
import { useTenant } from "@src/features/tenants";
import type { ScenarioRow } from "../utils/scenario-group";
import { useScenarioCoordinatesStore, EMPTY_SCENARIO_CONFIG } from "../stores/scenario-coordinates-store";

interface MapCanvasProps {
  rows: ScenarioRow[];
  selectedScenarioUuid?: string | null;
  onScenarioSelect: (scenarioUuid: string) => void;
  /** When set, cursor switches to crosshair and map clicks call this back. */
  onMapClick?: (lngLat: LngLat) => void;
  /** Override the container height. Defaults to flex:1. */
  height?: number;
}

/**
 * Australia bounding box. Default view when there are no pins yet.
 */
const AUSTRALIA_BBOX: [number, number, number, number] = [112.0, -44.0, 154.0, -10.0];

/**
 * Default fallback fit. Used by the init effect (no initial pins) and the
 * auto-fit effect when the active tenant has zero pins (e.g. tenant switch
 * from a region with pins to one without).
 */
type Coord = { lat: number; lng: number };

function fitDefaultBounds(map: MlMap, animated: boolean) {
  map.fitBounds(AUSTRALIA_BBOX, { padding: 40, ...(animated ? { duration: 800 } : {}) });
}

/**
 * Compute the right viewport for a set of pins:
 *   - 0 pins → default Australia bbox
 *   - 1 pin  → centre + street-scale zoom (degenerate bbox would zoom to ~22)
 *   - 2+ pins → fit-bounds with maxZoom guard so two close pins don't snap to a single street
 *
 * Returns either a `flyTo` (animated) or a `MapOptions` partial (consumed by
 * the constructor) depending on `mode`. The init effect uses constructor
 * options because the map isn't built yet; the auto-fit effect uses flyTo /
 * fitBounds to animate gently.
 */
function applyPinViewport(map: MlMap | null, pins: Coord[], mode: "animate" | "default") {
  if (!map) return;
  if (pins.length === 0) {
    if (mode === "animate") fitDefaultBounds(map, true);
    return;
  }
  if (pins.length === 1) {
    map.flyTo({ center: [pins[0].lng, pins[0].lat], zoom: 13, duration: mode === "animate" ? 800 : 0 });
    return;
  }
  const lats = pins.map((p) => p.lat);
  const lngs = pins.map((p) => p.lng);
  map.fitBounds([Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)], {
    padding: 60,
    maxZoom: 14,
    duration: mode === "animate" ? 800 : 0,
  });
}

function makeMarkerElement(colour: string): HTMLDivElement {
  const el = document.createElement("div");
  el.style.width = "18px";
  el.style.height = "18px";
  el.style.borderRadius = "50%";
  el.style.border = "2px solid white";
  el.style.background = colour;
  el.style.boxShadow = "0 1px 4px rgba(0,0,0,0.4)";
  el.style.cursor = "pointer";
  return el;
}

const STYLE_URL = "https://api.maptiler.com/maps/hybrid/style.json?key=get_your_own_OpIi9ZULNHzrESv6T2vL";
/**
 * Pure-OSM raster fallback when STYLE_URL fails (e.g. demo key exhausted).
 * No satellite imagery, but always renders.
 */
const FALLBACK_STYLE = {
  version: 8 as const,
  sources: {
    osm: {
      type: "raster" as const,
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [{ id: "osm", type: "raster" as const, source: "osm" }],
};

/**
 * Shared MapLibre canvas used by both the Grid view (read-only strip) and the
 * Setup view (with click-to-place editing). Owns the map instance, marker
 * lifecycle, and tile fallback. The Setup-only chrome (un-positioned list,
 * lat/lng inputs, save/cancel) lives in ScenarioMap.web.tsx.
 */
export function MapCanvas({ rows, selectedScenarioUuid, onScenarioSelect, onMapClick, height }: MapCanvasProps) {
  const { theme } = useUnistyles();
  const { selectedTenant } = useTenant();
  const tenantId = selectedTenant?.id ?? "";
  const configByUuid = useScenarioCoordinatesStore((s) => s.byTenant[tenantId] ?? EMPTY_SCENARIO_CONFIG);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const markersRef = useRef<Map<string, MlMarker>>(new Map());
  // Click handlers parallel to markersRef so the sync effect can swap a stale
  // closure (capturing the first render's onScenarioSelect) for a fresh one
  // when the existing-marker branch runs.
  const markerClickHandlersRef = useRef<Map<string, (ev: MouseEvent) => void>>(new Map());

  // Computed once per render so the init effect, the auto-fit effect, and
  // the marker-sync effect all see the same list. Identity stays stable
  // when rows/configByUuid don't change, so the auto-fit effect doesn't
  // re-fire spuriously.
  const pins: Coord[] = useMemo(
    () => rows.map((r) => configByUuid[r.scenarioUuid]?.coordinate).filter((c): c is Coord => !!c),
    [rows, configByUuid],
  );

  // Effect 1 — Initialise the map once, with an initial viewport derived
  // from the pins on first render. We deliberately don't re-fit on later
  // renders: once the user has panned/zoomed we shouldn't yank them back.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const baseOptions: maplibregl.MapOptions = {
      container: containerRef.current,
      style: STYLE_URL,
    };
    if (pins.length === 0) {
      baseOptions.bounds = AUSTRALIA_BBOX;
      baseOptions.fitBoundsOptions = { padding: 40 };
    } else if (pins.length === 1) {
      baseOptions.center = [pins[0].lng, pins[0].lat];
      baseOptions.zoom = 13;
    } else {
      const lats = pins.map((p) => p.lat);
      const lngs = pins.map((p) => p.lng);
      baseOptions.bounds = [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
      baseOptions.fitBoundsOptions = { padding: 60, maxZoom: 14 };
    }

    let map: MlMap;
    try {
      map = new maplibregl.Map(baseOptions);
      map.on("error", (e) => {
        if (e.error?.message?.includes("style") || e.error?.status === 401) {
          map.setStyle(FALLBACK_STYLE);
        }
      });
    } catch {
      map = new maplibregl.Map({ ...baseOptions, style: FALLBACK_STYLE });
    }
    mapRef.current = map;
    // Snapshot the marker registry now so cleanup uses the same instance
    // that this effect populated, even if a later render swaps the ref.
    const markers = markersRef.current;
    const handlers = markerClickHandlersRef.current;
    return () => {
      map.remove();
      mapRef.current = null;
      markers.clear();
      handlers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberate: only fit once on mount
  }, []);

  // Effect 2 — Auto-fit on later renders, but ONLY in two cases:
  //   (a) zero → non-zero pin transition within the same mount (user added
  //       their first pin), gently fly there.
  //   (b) tenant switch — pins change wholesale to a new region, re-fit.
  // Subsequent renders that don't match either case leave the viewport alone
  // (so user pan/zoom isn't clobbered).
  const prevPinCountRef = useRef<number | null>(null);
  const prevTenantIdRef = useRef(tenantId);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const tenantChanged = prevTenantIdRef.current !== tenantId;
    const isFirstRender = prevPinCountRef.current === null;
    const prevCount = prevPinCountRef.current ?? pins.length;
    prevPinCountRef.current = pins.length;
    prevTenantIdRef.current = tenantId;

    // First render's viewport is handled by the init effect above.
    if (isFirstRender && !tenantChanged) return;
    // Otherwise only re-fit on a real trigger:
    const tenantSwitched = tenantChanged;
    const justAddedFirstPin = !tenantChanged && prevCount === 0 && pins.length > 0;
    if (!tenantSwitched && !justAddedFirstPin) return;

    applyPinViewport(map, pins, "animate");
  }, [pins, tenantId]);

  // Click-to-place mode toggle
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!onMapClick) {
      map.getCanvas().style.cursor = "";
      return;
    }
    map.getCanvas().style.cursor = "crosshair";
    const handler = (e: maplibregl.MapMouseEvent) => onMapClick(e.lngLat);
    map.on("click", handler);
    return () => {
      map.off("click", handler);
      if (mapRef.current) mapRef.current.getCanvas().style.cursor = "";
    };
  }, [onMapClick]);

  // Effect 4 — Reconcile DOM markers with the current rows. Two passes:
  //   1. for each positioned row: create-or-update its marker, swap stale click handler
  //   2. drop markers for rows that disappeared (e.g. site filter change)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const markers = markersRef.current;
    const handlers = markerClickHandlersRef.current;
    const seen = new Set<string>();

    for (const row of rows) {
      const c = configByUuid[row.scenarioUuid]?.coordinate;
      if (!c) continue;
      seen.add(row.scenarioUuid);
      const isSelected = selectedScenarioUuid === row.scenarioUuid;
      const colour = isSelected ? theme.colors.icon : "#1f6feb";
      const title = `${row.scenarioName} — ${row.cameraDisplayName}`;
      // The click handler closes over `onScenarioSelect`, which has fresh
      // identity each render — we always want the latest one bound, so the
      // handler is rebuilt + re-attached every pass.
      const onClick = (ev: MouseEvent) => {
        ev.stopPropagation();
        onScenarioSelect(row.scenarioUuid);
      };

      const existing = markers.get(row.scenarioUuid);
      if (existing) {
        // Update in place: position + appearance, then swap the stale click
        // handler (its closure captured the first render's onScenarioSelect).
        existing.setLngLat([c.lng, c.lat]);
        const el = existing.getElement();
        el.style.background = colour;
        el.title = title;
        const prev = handlers.get(row.scenarioUuid);
        if (prev) el.removeEventListener("click", prev);
        el.addEventListener("click", onClick);
        handlers.set(row.scenarioUuid, onClick);
      } else {
        // First time we see this row: create the DOM element and attach.
        const el = makeMarkerElement(colour);
        el.title = title;
        el.addEventListener("click", onClick);
        handlers.set(row.scenarioUuid, onClick);
        const marker = new maplibregl.Marker({ element: el }).setLngLat([c.lng, c.lat]).addTo(map);
        markers.set(row.scenarioUuid, marker);
      }
    }

    // Drop markers for rows that disappeared.
    for (const [uuid, marker] of markers.entries()) {
      if (!seen.has(uuid)) {
        marker.remove();
        markers.delete(uuid);
        handlers.delete(uuid);
      }
    }
  }, [rows, configByUuid, selectedScenarioUuid, onScenarioSelect, theme.colors.icon]);

  return (
    <View
      style={{
        flex: height === undefined ? 1 : undefined,
        height,
        minHeight: 240,
        borderRadius: theme.spacing.md,
        overflow: "hidden",
        borderWidth: 1,
        borderColor: theme.colors.borderCard,
      }}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
    </View>
  );
}
